import { Field, Formik } from 'formik'
import type { FormikErrors, FormikTouched } from 'formik'
import React, { useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import * as Yup from 'yup'

import { Button, Fieldset, Input, Select } from '@fourthwall/components'
import { CloseIcon, TickIcon } from '@fourthwall/icons'

import type { SectionSetting } from '../../../types'
import { getFormValues } from '../../../utils/FormValues'
import type { FormValues } from '../../../utils/FormValues'
import * as S from './InlineSettingEditor.styled'
import { CheckboxForm } from './forms/CheckboxForm'
import { ColorForm } from './forms/ColorForm'
import { HeaderForm } from './forms/HeaderForm'
import { NumberForm } from './forms/NumberForm'
import { RadioForm } from './forms/RadioForm'
import { RangeForm } from './forms/RangeForm'
import { SelectForm } from './forms/SelectForm'
import { TextForm } from './forms/TextForm'

type InlineSettingEditorProps = {
  setting?: SectionSetting
  onSave: (setting: SectionSetting) => void
  onClose?: () => void
  icon?: React.ReactNode
  header?: React.ReactNode
  create?: boolean
}

const SETTING_TYPES: Record<SectionSetting['type'], { label: string }> = {
  header: { label: 'Header' },
  text: { label: 'Text Input' },
  number: { label: 'Number Input' },
  color: { label: 'Color Picker' },
  checkbox: { label: 'Checkbox' },
  radio: { label: 'Radio Buttons' },
  select: { label: 'Select Dropdown' },
  range: { label: 'Range Slider' },
}

const validationSchema = Yup.object().shape({
  type: Yup.string().required('Setting type is required'),
  id: Yup.string().when('type', {
    is: (type: string) => type !== 'header',
    then: (schema) => schema.required('Setting ID is required'),
    otherwise: (schema) => schema.optional(),
  }),
  // optionals
  content: Yup.string().when('type', {
    is: 'header',
    then: (schema) => schema.required('Content is required for headers'),
    otherwise: (schema) => schema.optional(),
  }),
  label: Yup.string().when('type', {
    is: (type: string) => type !== 'header',
    then: (schema) => schema.required('Label is required'),
    otherwise: (schema) => schema.optional(),
  }),
  options: Yup.array().of(
    Yup.object().shape({
      value: Yup.string().required('Value is required'),
      label: Yup.string().required('Label is required'),
    }),
  ),
})

type FormInnerProps = {
  values: FormValues
  setFieldValue: (field: string, value: any) => void
  errors: FormikErrors<FormValues>
  touched: FormikTouched<FormValues>
  create?: boolean
}

const FormInner = ({ values, setFieldValue, errors, touched, create }: FormInnerProps) => {
  const renderForm = () => {
    switch (values.type) {
      case 'color':
        return <ColorForm setFieldValue={setFieldValue} values={values} />
      case 'header':
        return <HeaderForm />
      case 'text':
        return <TextForm />
      case 'number':
        return <NumberForm />
      case 'range':
        return <RangeForm />
      case 'radio':
        return <RadioForm setFieldValue={setFieldValue} values={values} />
      case 'select':
        return <SelectForm setFieldValue={setFieldValue} values={values} />
      case 'checkbox':
        return <CheckboxForm />
      default:
        return null
    }
  }

  const renderTypeSelector = () => {
    return (
      <Fieldset.Row>
        <Fieldset.Item>
          <Select
            label="Setting Type"
            options={Object.entries(SETTING_TYPES).map(([type, { label }]) => ({
              value: type,
              label,
            }))}
            value={values.type}
            onValueChange={(value) => setFieldValue('type', value)}
            disabled={!create}
          />
        </Fieldset.Item>
      </Fieldset.Row>
    )
  }

  const renderIdField = () => {
    return (
      <Fieldset.Row>
        <Fieldset.Item>
          <Field
            name="id"
            component={Input}
            label="Setting ID"
            error={touched.id && errors.id ? errors.id : undefined}
            disabled={!create}
          />
        </Fieldset.Item>
      </Fieldset.Row>
    )
  }

  return (
    <S.EditorContent>
      <Fieldset>
        {renderTypeSelector()}
        {values.type !== 'header' && renderIdField()}
        {renderForm()}
      </Fieldset>
    </S.EditorContent>
  )
}

export const InlineSettingEditor = ({
  setting,
  onSave,
  onClose,
  icon,
  header,
  create,
}: InlineSettingEditorProps) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const initialValues = getFormValues(setting)

  const handleSubmit = (values: typeof initialValues) => {
    const newSetting: SectionSetting = {
      ...values,
      id: values.type === 'header' ? uuidv4() : values.id,
    } as SectionSetting

    onSave(newSetting)
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, submitForm, errors, touched }) => (
        <S.SettingItem>
          <S.SettingItemHeader>
            <S.EditHandle>{icon}</S.EditHandle>
            {header}
            <S.ButtonContainer>
              <Button
                appearance="destructive-inverted"
                size="small"
                leftIcon={<CloseIcon />}
                onClick={onClose}
              />
              <Button
                appearance="primary"
                size="small"
                leftIcon={<TickIcon height={12} />}
                onClick={submitForm}
              />
            </S.ButtonContainer>
          </S.SettingItemHeader>
          <S.EditingContainer ref={parentRef}>
            <FormInner
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              create={create}
            />
          </S.EditingContainer>
        </S.SettingItem>
      )}
    </Formik>
  )
}
